import type { EconomyConfig } from '../config/types';

export interface ValueMultiplierInput {
  siteIndex: number;
  shrineRichVeins?: number;
  heroHome?: boolean;
  goldenLantern?: boolean;
  adBoostActive?: boolean;
}

export interface MultiplierFns {
  siteValueMult: (siteIndex: number) => number;
  valueMultiplier: (input: ValueMultiplierInput) => number;
  stationMultiplier: (opts: {
    managed: boolean;
    hasTrait: boolean;
    badgeLevel: number;
    rallyActive: boolean;
  }) => number;
}

interface IapProduct {
  id?: string;
  grants?: { permanentIncomeMultiplier?: number };
}

/** Canonical IAP product id for Golden Lantern (lookup key, not a balance number). */
export const GOLDEN_LANTERN_PRODUCT_ID = 'com.burrowco.goldenlantern';

/** Income multiplier pieces used by golden / income surfaces. */
export function createMultipliers(e: EconomyConfig): MultiplierFns {
  const homeBonus = e.hero.homeIncomeBonus;

  const richVeinsUpgrade = e.shrine.upgrades.find((u) => u.id === 'richVeins');
  if (!richVeinsUpgrade || typeof richVeinsUpgrade.perLevel !== 'number') {
    throw new Error('multipliers: shrine upgrade richVeins.perLevel required (no fallback)');
  }
  const richVeinsPer = richVeinsUpgrade.perLevel;

  const iap = e.iap as { products?: IapProduct[] } | undefined;
  const lanternProduct = iap?.products?.find((p) => p.id === GOLDEN_LANTERN_PRODUCT_ID);
  const lanternMultRaw = lanternProduct?.grants?.permanentIncomeMultiplier;
  if (typeof lanternMultRaw !== 'number') {
    throw new Error(
      `multipliers: IAP product ${GOLDEN_LANTERN_PRODUCT_ID} permanentIncomeMultiplier required`,
    );
  }
  const lanternMult: number = lanternMultRaw;

  const adMult = e.boosts.adBoostMultiplier;
  const traitBonus = e.managers.traitBonus;
  const badgePer = e.managers.badges.bonusPerLevel;
  const abilityMult = e.managers.ability.multiplier;

  function siteValueMult(siteIndex: number): number {
    return e.migration.siteValueGrowth ** siteIndex;
  }

  function valueMultiplier(input: ValueMultiplierInput): number {
    const shrine = 1 + richVeinsPer * (input.shrineRichVeins ?? 0);
    const home = input.heroHome ? 1 + homeBonus : 1;
    const lantern = input.goldenLantern ? lanternMult : 1;
    const boost = input.adBoostActive ? adMult : 1;
    return siteValueMult(input.siteIndex) * shrine * home * lantern * boost;
  }

  function stationMultiplier(opts: {
    managed: boolean;
    hasTrait: boolean;
    badgeLevel: number;
    rallyActive: boolean;
  }): number {
    if (!opts.managed) return 1;
    const trait = opts.hasTrait ? traitBonus : 0;
    const badge = Math.max(0, opts.badgeLevel - 1) * badgePer;
    const base = 1 + trait + badge;
    return opts.rallyActive ? base * abilityMult : base;
  }

  return { siteValueMult, valueMultiplier, stationMultiplier };
}
